/**
 * Guest funnel: GA (marketing) + backend beacons (admin dashboard).
 */
import { trackEvent } from './analytics';
import config from '../config';

const CATEGORY = 'Guest Funnel';
const VISITOR_KEY_STORAGE = 'velox_guest_funnel_vid';

export function getGuestFunnelVisitorKey() {
  try {
    let k = sessionStorage.getItem(VISITOR_KEY_STORAGE);
    if (!k) {
      k =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      sessionStorage.setItem(VISITOR_KEY_STORAGE, k);
    }
    return k;
  } catch {
    return null;
  }
}

function beaconGuestFunnel(eventType, nfcUID = null, value = null, options = {}) {
  const visitorKey = options.visitorKey !== undefined ? options.visitorKey : getGuestFunnelVisitorKey();
  const url = `${config.apiBaseUrl}/api/guest/funnel-event`;
  const body = JSON.stringify({
    event: eventType,
    ...(nfcUID ? { nfcUID } : {}),
    ...(value != null && Number.isFinite(value) ? { value } : {}),
    ...(visitorKey ? { visitorKey } : {}),
  });
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch {
    /* fall through */
  }
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {});
}

export function trackGuestFunnel(action, label = null, value = null) {
  trackEvent(CATEGORY, action, label, value);
}

/** @param {string} nfcUID */
export function trackPublicNfcCardViewed(nfcUID) {
  trackGuestFunnel('nfc_public_card_viewed', nfcUID);
  beaconGuestFunnel('nfc_public_card_viewed', nfcUID, null, { visitorKey: getGuestFunnelVisitorKey() });
}

/** A /c/{userId} open. Kept separate from NFC taps. */
export function trackPublicDigitalCardViewed(userId) {
  trackGuestFunnel('digital_public_card_viewed', userId);
  beaconGuestFunnel('digital_public_card_viewed', userId, null, { visitorKey: getGuestFunnelVisitorKey() });
}

export function trackAddToContactsClick(nfcUID) {
  trackGuestFunnel('guest_add_to_contacts_click', nfcUID);
}

export function trackAddToContactsSuccess(nfcUID) {
  trackGuestFunnel('guest_add_to_contacts_success', nfcUID);
}

export function trackGuestSaveModalOpen(nfcUID) {
  trackGuestFunnel('guest_save_modal_open', nfcUID);
  beaconGuestFunnel('guest_save_modal_open', nfcUID);
}

/** Email submitted on Save to Velox sheet (before server response). */
export function trackGuestEmailSubmitted(nfcUID) {
  trackGuestFunnel('guest_email_submitted', nfcUID);
  beaconGuestFunnel('guest_email_submitted', nfcUID);
}

/** Server persisted guest vault + contact immediately. */
export function trackGuestImmediateSaveSuccess(nfcUID) {
  trackGuestFunnel('guest_immediate_save_success', nfcUID);
}

/** Confirmation email accepted by mail provider (response emailSent true). */
export function trackGuestFollowupEmailSent(nfcUID) {
  trackGuestFunnel('guest_followup_email_sent', nfcUID);
}

/**
 * Email continuation link opened (/guest/save?t=…).
 * @param {'success'|'invalid'|'expired'|'error'} outcome
 */
export function trackGuestContinuationLinkUsed(outcome, label = null) {
  trackGuestFunnel('guest_continuation_link_used', `${outcome}${label ? `_${label}` : ''}`);
}

/** @deprecated use trackGuestContinuationLinkUsed */
export function trackGuestMagicLinkVerify(outcome, label = null) {
  trackGuestContinuationLinkUsed(outcome, label);
}

/** Legacy: first-time verify path completed save (rare after immediate-save rollout). */
export function trackGuestVaultSaveLegacyVerified(nfcUID) {
  trackGuestFunnel('guest_vault_save_legacy_verify', nfcUID);
}

/**
 * Merge analytics: authoritative counts are recorded on the server (login / verify-email).
 * Client keeps GA-only signals here for marketing dashboards.
 * @param {object} s
 * @param {number} s.imported
 * @param {number} s.skipped
 * @param {number} s.failed
 * @param {boolean} [s.pendingRetry]
 */
export function trackGuestMergeComplete(s) {
  if (!s || !s.attempted) return;
  trackGuestFunnel('guest_merge_attempt', null, s.imported + s.skipped + s.failed);
  if (s.imported > 0) trackGuestFunnel('guest_merge_imported', null, s.imported);
  if (s.skipped > 0) trackGuestFunnel('guest_merge_skipped', null, s.skipped);
  if (s.failed > 0) trackGuestFunnel('guest_merge_failed', null, s.failed);
  if (s.pendingRetry) trackGuestFunnel('guest_merge_pending_retry', null, 1);
}
